import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ChatHeader } from '../components/chat/ChatHeader';
import { ChatInput } from '../components/chat/ChatInput';
import { MessageList } from '../components/chat/MessageList';
import { SessionSidebar } from '../components/chat/SessionSidebar';
import { useSSEStream } from '../hooks/useSSEStream';
import { AGENTS, type AgentInfo } from '../lib/agents';
import { api } from '../lib/api';
import { useAuth } from '../providers/AuthProvider';
import { getUserTier, useAuthStore } from '../stores/auth.store';
import { type SessionMessage, useSessionStore } from '../stores/sessionStore';

interface ApiJourney {
  id: string;
  name: string;
  desiredOutcome: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * SessionPage — the main chat interface.
 *
 * Split layout: chat area (left, ~70%) + sidebar (right, ~30%).
 * Supports both authenticated and guest modes.
 * Streams agent responses via SSE with typing indicator.
 */
export function SessionPage(): React.JSX.Element {
  const { agentId, id: sessionIdParam } = useParams<{ agentId?: string; id?: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isGuest, setGuestMode } = useAuth();

  const {
    messages,
    isStreaming,
    sessionId,
    addMessage,
    setStreaming,
    setSessionId,
    clearSession,
  } = useSessionStore();

  const [streamingText, setStreamingText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sessionTitle, setSessionTitle] = useState<string | undefined>();
  const [insights] = useState<{ id: string; text: string }[]>([]);
  const [journeyName, setJourneyName] = useState<string | undefined>();
  const [desiredOutcome, setDesiredOutcome] = useState<string | undefined>();

  // Journey picker state for Early Testers creating new sessions
  const authState = useAuthStore();
  const tier = getUserTier(authState);
  const isEarlyTester = tier === 'early_tester';
  const [availableJourneys, setAvailableJourneys] = useState<ApiJourney[]>([]);
  const [selectedJourneyId, setSelectedJourneyId] = useState<string | null>(null);
  const [showJourneyPicker, setShowJourneyPicker] = useState(false);
  const [resolvedAgent, setResolvedAgent] = useState<AgentInfo | undefined>(undefined);

  // Resolve agent from route params (for /agents/:agentId/chat) or loaded session data
  const agent: AgentInfo | undefined = useMemo(() => {
    if (agentId) return AGENTS.find((a) => a.id === agentId);
    return resolvedAgent;
  }, [agentId, resolvedAgent]);

  // Enter guest mode if not authenticated and no guest flag yet
  useEffect(() => {
    if (!isAuthenticated && !isGuest) {
      setGuestMode(true);
    }
  }, [isAuthenticated, isGuest, setGuestMode]);

  // Fetch available journeys for Early Testers (new session only)
  useEffect(() => {
    if (!isEarlyTester || sessionIdParam) return; // Skip if loading existing session
    async function fetchJourneys() {
      try {
        const journeys = await api.get<ApiJourney[]>('/journeys?status=Active');
        setAvailableJourneys(journeys);
        if (journeys.length > 0) {
          setShowJourneyPicker(true);
        }
      } catch {
        // silently fail — no journey picker
      }
    }
    fetchJourneys();
  }, [isEarlyTester, sessionIdParam]);

  // Initialize session (create or load)
  useEffect(() => {
    let cancelled = false;

    async function initSession() {
      // If we have a session ID from the URL, load it
      if (sessionIdParam) {
        try {
          const data = await api.get<{
            id: string;
            title?: string;
            agentId: string;
            agentName: string;
            journeyId?: string;
            messages: Array<{ id: string; role: string; content: string; createdAt: string }>;
          }>(`/sessions/${sessionIdParam}`);

          if (cancelled) return;
          setSessionId(data.id);
          setSessionTitle(data.title);

          // Resolve agent from the response (match by name, case-insensitive)
          const matchedAgent = AGENTS.find(
            (a) => a.name.toLowerCase() === data.agentName.toLowerCase()
          );
          if (matchedAgent) {
            setResolvedAgent(matchedAgent);
          }

          // Load existing messages (map API format to store format)
          data.messages.forEach((msg) => addMessage({
            id: msg.id,
            role: msg.role as 'user' | 'assistant',
            content: msg.content,
            created_at: msg.createdAt,
          }));
        } catch {
          if (!cancelled) setError('Failed to load session');
        }
        return;
      }

      // For guest mode, just set a local session ID — no backend call needed
      if (isGuest && agent) {
        setSessionId(`guest-${Date.now()}`);
        return;
      }

      // Authenticated user — if journey picker is showing, wait for user to proceed
      if (isAuthenticated && agent && !sessionId && showJourneyPicker) {
        // Don't create session yet — wait for user to dismiss journey picker
        return;
      }

      // Authenticated user, create a new session (no journey picker or already dismissed)
      if (isAuthenticated && agent && !sessionId && !showJourneyPicker) {
        try {
          const payload: { agent_id: string; journeyId?: string } = {
            agent_id: agent.id,
          };
          if (selectedJourneyId) {
            payload.journeyId = selectedJourneyId;
            // Set journey context in sidebar
            const journey = availableJourneys.find((j) => j.id === selectedJourneyId);
            if (journey) {
              setJourneyName(journey.name);
              setDesiredOutcome(journey.desiredOutcome);
            }
          }
          const data = await api.post<{ id: string }>('/sessions', payload);
          if (cancelled) return;
          setSessionId(data.id);
        } catch {
          if (!cancelled) setError('Failed to create session');
        }
      }
    }

    initSession();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdParam, agent?.id, isGuest, isAuthenticated, showJourneyPicker]);

  // Clean up session on unmount
  useEffect(() => {
    return () => {
      clearSession();
    };
  }, [clearSession]);

  // Add the agent's intro message when the session starts empty (new sessions only)
  useEffect(() => {
    if (agent && messages.length === 0 && sessionId && !isStreaming && !sessionIdParam) {
      const introMessage: SessionMessage = {
        id: `intro-${agent.id}`,
        role: 'assistant',
        content: getAgentIntro(agent),
        created_at: new Date().toISOString(),
      };
      addMessage(introMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id, sessionId, sessionIdParam]);

  // SSE streaming hook
  const { send, sendGuest, abort } = useSSEStream({
    onDelta: (text) => {
      setStreamingText((prev) => prev + text);
    },
    onDone: (fullText) => {
      const assistantMsg: SessionMessage = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: fullText,
        created_at: new Date().toISOString(),
      };
      addMessage(assistantMsg);
      setStreamingText('');
      setStreaming(false);
    },
    onError: (errMsg) => {
      setError(errMsg);
      setStreamingText('');
      setStreaming(false);
    },
  });

  // Clean up stream on unmount
  useEffect(() => {
    return () => abort();
  }, [abort]);

  const handleSend = useCallback(
    (content: string) => {
      if (!agent) return;

      // Add user message to local state immediately
      const userMsg: SessionMessage = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content,
        created_at: new Date().toISOString(),
      };
      addMessage(userMsg);

      // Start streaming
      setStreaming(true);
      setError(null);
      setStreamingText('');

      if (isGuest) {
        // Guest mode: send directly via guest endpoint with history
        const history = messages
          .filter((m) => !m.id.startsWith('intro-'))
          .map((m) => ({ role: m.role, content: m.content }));
        sendGuest(agent.id, content, history);
      } else {
        // Authenticated mode: send via session endpoint
        if (!sessionId) return;
        send(sessionId, content);
      }
    },
    [agent, isGuest, sessionId, messages, addMessage, setStreaming, send, sendGuest],
  );

  const handleSwitchAgent = useCallback(
    (newAgentId: string) => {
      clearSession();
      navigate(`/agents/${newAgentId}/chat`);
    },
    [clearSession, navigate],
  );

  const handleSignUp = useCallback(() => {
    navigate('/register');
  }, [navigate]);

  // Loading / error states
  if (!agent) {
    // If loading a session by ID, show loading state (agent resolves async)
    if (sessionIdParam) {
      return (
        <div className="flex h-full items-center justify-center">
          <p className="text-text-muted">Loading session…</p>
        </div>
      );
    }
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-text-muted">Agent not found</p>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Journey Picker overlay — shown for Early Testers before session starts */}
      {showJourneyPicker && !sessionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-lg border border-border bg-surface-raised p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-text-primary mb-1">
              Link this session to a Journey?
            </h2>
            <p className="text-sm text-text-muted mb-4">
              Journeys help you track related sessions and insights over time.
            </p>

            {availableJourneys.length > 0 && (
              <div className="mb-4 max-h-48 overflow-y-auto space-y-2">
                {availableJourneys.map((j) => (
                  <label
                    key={j.id}
                    className={`flex items-start gap-3 rounded border p-3 cursor-pointer transition ${
                      selectedJourneyId === j.id
                        ? 'border-gail bg-gail-light/30'
                        : 'border-border hover:border-gail/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="journey"
                      value={j.id}
                      checked={selectedJourneyId === j.id}
                      onChange={() => setSelectedJourneyId(j.id)}
                      className="mt-0.5 accent-gail"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary">{j.name}</p>
                      <p className="text-xs text-text-muted truncate">{j.desiredOutcome}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setSelectedJourneyId(null);
                  setShowJourneyPicker(false);
                }}
                className="rounded border border-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-muted"
              >
                Skip
              </button>
              <button
                onClick={() => setShowJourneyPicker(false)}
                className="rounded bg-gail px-4 py-2 text-sm font-medium text-white hover:bg-gail-accent"
              >
                {selectedJourneyId ? 'Start with Journey' : 'Start Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat area (left, ~70%) */}
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        <ChatHeader
          agentName={agent.name}
          agentColour={agent.colour}
          coreMessage={agent.coreMessage}
          sessionTitle={sessionTitle}
          hint={isEarlyTester ? 'Hover over a response to save it as an Insight 💡 or Action ✅' : undefined}
        />

        <MessageList
          messages={messages}
          isStreaming={isStreaming}
          streamingText={streamingText}
          agentColour={agent.colour}
          agentBgClass={agent.bgLightClass}
          agentName={agent.name}
          sessionId={sessionId}
        />

        {/* Error display */}
        {error && (
          <div className="px-4 py-2 text-sm text-red-600 bg-red-50 border-t border-red-100">
            Something went wrong — {error}.{' '}
            <button
              onClick={() => setError(null)}
              className="underline hover:no-underline"
            >
              Dismiss
            </button>
          </div>
        )}

        <ChatInput
          onSend={handleSend}
          disabled={isStreaming || !sessionId}
          agentColour={agent.colour}
          agentButtonVariant={agent.buttonVariant}
        />
      </div>

      {/* Sidebar (right, ~30%) */}
      <SessionSidebar
        isGuest={isGuest}
        currentAgent={agent}
        journeyName={journeyName}
        desiredOutcome={desiredOutcome}
        insights={insights}
        onSwitchAgent={handleSwitchAgent}
        onSignUp={handleSignUp}
        className="hidden w-[320px] flex-shrink-0 lg:flex"
      />

      {/* Guest mode banner (mobile) */}
      {isGuest && (
        <div className="fixed bottom-20 left-4 right-4 lg:hidden">
          <div className="rounded-lg bg-surface-raised border border-border shadow-md px-4 py-3 flex items-center justify-between">
            <p className="text-xs text-text-secondary">
              Join Us to save your progress
            </p>
            <button
              onClick={handleSignUp}
              className="text-xs font-medium underline text-text-primary"
            >
              Join Us
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Returns an intro message for the agent based on their personality.
 */
function getAgentIntro(agent: AgentInfo): string {
  const intros: Record<string, string> = {
    ray: `Red conversations help you build more of the things you need into your work — tools, practices, processes, relationships, resources — help you organize your thoughts, gain insights, and brainstorm immediate steps you can take, right now, to feel more solid at work.\n\nWhat's on your mind?`,
    olympia: `Orange conversations build more of what you want into your work, so your work motivates and energizes you, helping you to organize your thoughts and gain insights to discover and rediscover what motivates you — and to brainstorm immediate steps you can take to get energized, right now.\n\nWhat's on your mind?`,
    yves: `Yellow conversations build more focus and clarity into your work — to help you organize your thoughts and gain insights that drive your work — and to brainstorm immediate steps you can take to laser focus on what you need to get done, right now.\n\nWhat's on your mind?`,
    gail: `Green conversations help you build strong connections, and find the right people to support your journey. They help you organize your thoughts and gain insights to stay connected at work — and to brainstorm immediate steps you can take to gain the support you need, right now.\n\nWhat's on your mind?`,
    timothy: `Turquoise conversations build more of you into what you do. Helping you organize your thoughts and gain insights to express what makes your contribution uniquely yours at work — and to brainstorm immediate steps you can take to bring more of your authentic self to work, right now.\n\nWhat's on your mind?`,
    byron: `Blue conversations build a sense of discovery, so you can explore new ideas, research possibilities, and find different approaches. They help you organize your thoughts and gain insights from a completely different lens — and brainstorm immediate steps you can take to open your mind to discovery and innovation, right now.\n\nWhat's on your mind?`,
    peter: `Purple conversations help you think about the big picture — what you've learned, what you can teach others, and how your work fits into something larger than yourself. They help you organize your thoughts and gain insights about how your experience matters, and how to share it — and to brainstorm immediate steps you can take to build your work legacy, right now.\n\nWhat's on your mind?`,
  };

  return intros[agent.id] ?? `I'm here to provide you perspectives and brainstorm immediate next steps. What's on your mind?`;
}
